import { Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import {
  CalendarUnit,
  CreateScheduleResponseDTO,
  EntityRefUtil,
  formatDateWithUserTimezone,
  GetScheduleFormFormsResponseDTO,
  RecordState,
  resolveFixedCalendarDueDate,
} from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { DragDropContext, Draggable, Droppable, DropResult } from '@hello-pangea/dnd';
import { zodResolver } from '@hookform/resolvers/zod';
import { TooltipArrow, TooltipPortal } from '@radix-ui/react-tooltip';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Controller, FormProvider, useFieldArray, useForm, useWatch } from 'react-hook-form';
import { NumericFormat } from 'react-number-format';
import { useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';

import { ArchiveConfirmationDialog } from '@/components/common/archive-confirmation-dialog';
import { RestoreConfirmationDialog } from '@/components/common/restore-confirmation-dialog';
import { SaveChangesDialog } from '@/components/common/save-changes-dialog';
import ArchiveIcon from '@/components/icons/archive';
import CalendarBlankIcon from '@/components/icons/calendar-blank';
import ClockIcon from '@/components/icons/clock';
import CloneIcon from '@/components/icons/clone';
import CloseIcon from '@/components/icons/close';
import EllipsisHorizontalIcon from '@/components/icons/ellipsis-horizontal';
import GaugeIcon from '@/components/icons/gauge';
import GripDotVerticalIcon from '@/components/icons/grip-dot-vertical';
import InformationIcon from '@/components/icons/information';
import PlusIcon from '@/components/icons/plus';
import QuestionIcon from '@/components/icons/question';
import { TimelineCircleIcon } from '@/components/icons/timeline-shapes/circle';
import { TimelineDiamondIcon } from '@/components/icons/timeline-shapes/diamond';
import { TimelineFidgetSpinnerIcon } from '@/components/icons/timeline-shapes/fidget-spinner';
import { TimelineHexagonIcon } from '@/components/icons/timeline-shapes/hexagon';
import { TimelineHourglassIcon } from '@/components/icons/timeline-shapes/hourglass';
import { TimelinePentagonIcon } from '@/components/icons/timeline-shapes/pentagon';
import { TimelineSquareIcon } from '@/components/icons/timeline-shapes/square';
import { TimelineStarIcon } from '@/components/icons/timeline-shapes/star';
import { TimelineTriangleIcon } from '@/components/icons/timeline-shapes/triangle';
import { TimelineTriangleDownIcon } from '@/components/icons/timeline-shapes/triangle-down';
import { UndoIcon } from '@/components/icons/undo';
import { FormTopbar } from '@/components/topbar/form-topbar';
import { useFormTopbarStore } from '@/components/topbar/form-topbar-store';
import { AutoResizeInput } from '@/components/ui/auto-resize-input';
import { Badge } from '@/components/ui/badge';
import { DatePicker } from '@/components/ui/date-picker';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Input, inputVariants } from '@/components/ui/input';
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@/components/ui/resizable';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select } from '@/components/ui/select';
import { useToast } from '@/components/ui/toaster';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { usePermissions } from '@/hooks/use-permissions';
import { cn } from '@/lib/utils';
import { checkKeys } from '@/queries/checks';
import { deploymentRuleKeys } from '@/queries/deployments';
import { getScheduleByIdQueryOptions, getScheduleFormFormsSelectQueryOptions, getUnitsSelectQueryOptions, scheduleKeys } from '@/queries/schedule';
import { ApiService } from '@/services/api-v2';
import { useUserStore } from '@/stores/user.store';
import { getWorkspaceRoute } from '@/utils/routes';

import {
  getFixedTypeOptions,
  isCalendarUnit,
  mapApiToForm,
  mapFormToApi,
  mapFormToUpdateApi,
  scheduleFormSchema,
  SPECIAL_TIME_UNITS,
  type ScheduleFormData,
} from './schedule-form-utils';

// Trigger type options
const TRIGGER_TYPE_OPTIONS: { label: string; value: 'crawling' | 'fixed' }[] = [
  { label: 'Crawling', value: 'crawling' },
  { label: 'Fixed', value: 'fixed' },
];

// Timeline shapes configuration (max 10 checks)
const TIMELINE_SHAPES = [
  { name: 'square', icon: TimelineSquareIcon },
  { name: 'circle', icon: TimelineCircleIcon },
  { name: 'triangle', icon: TimelineTriangleIcon },
  { name: 'diamond', icon: TimelineDiamondIcon },
  { name: 'hourglass', icon: TimelineHourglassIcon },
  { name: 'pentagon', icon: TimelinePentagonIcon },
  { name: 'fidget-spinner', icon: TimelineFidgetSpinnerIcon },
  { name: 'hexagon', icon: TimelineHexagonIcon },
  { name: 'triangle-down', icon: TimelineTriangleDownIcon },
  { name: 'star', icon: TimelineStarIcon },
] as const;

function getTimelineShapeIcon(index: number) {
  // Handle negative indices (e.g., when findIndex returns -1 for deleted items)
  const safeIndex = index < 0 ? 0 : index % TIMELINE_SHAPES.length;
  return TIMELINE_SHAPES[safeIndex].icon;
}

// Members can view the schedules register but not the editor — wrap the
// editor body so the redirect-on-mount lives outside the giant hooks block
// (early-returns before later hooks would violate rules-of-hooks).
export function ScheduleEditorPage() {
  const { canManageSchedules } = usePermissions();
  const navigate = useNavigate();

  useEffect(() => {
    if (!canManageSchedules) {
      navigate('/schedules', { replace: true });
    }
  }, [canManageSchedules, navigate]);

  if (!canManageSchedules) return null;

  return <ScheduleEditorPageContent />;
}

function ScheduleEditorPageContent() {
  const userTimezone = useUserStore.getState().user?.timezone;
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [openLeaveDialog, setOpenLeaveDialog] = useState(false);
  const [openSaveConfirmDialog, setOpenSaveConfirmDialog] = useState(false);
  const [openArchiveConfirmation, setOpenArchiveConfirmation] = useState(false);
  const [openRestoreConfirmation, setOpenRestoreConfirmation] = useState(false);
  const [previewCycleCount, setPreviewCycleCount] = useState(1);
  const [pendingFormData, setPendingFormData] = useState<z.infer<typeof scheduleFormSchema> | null>(null);
  const formRef = useRef<HTMLFormElement>(null);

  const form = useForm<z.infer<typeof scheduleFormSchema>>({
    resolver: zodResolver(scheduleFormSchema),
    defaultValues: {
      name: 'Untitled Schedule',
      triggers: [{ id: '1', interval: 12, unitId: 'months', type: 'crawling' }],
      checks: [
        { id: 'check-a', label: 'A', formId: '' },
        { id: 'check-b', label: 'B', formId: '' },
        { id: 'check-c', label: 'C', formId: '' },
      ],
      cycle: [
        { id: 'cycle-1', checkId: 'check-a' },
        { id: 'cycle-2', checkId: 'check-b' },
        { id: 'cycle-3', checkId: 'check-a' },
        { id: 'cycle-4', checkId: 'check-c' },
      ],
    },
  });

  const {
    watch,
    setValue,
    control,
    formState: { isDirty, errors, isSubmitted },
  } = form;

  const name = watch('name');
  const triggers = useWatch({ control, name: 'triggers' });
  const checks = useWatch({ control, name: 'checks' });
  const cycle = useWatch({ control, name: 'cycle' });

  const triggersFieldArray = useFieldArray({ control, name: 'triggers' });
  const checksFieldArray = useFieldArray({ control, name: 'checks' });
  const cycleFieldArray = useFieldArray({ control, name: 'cycle' });

  // Determine if we're editing an existing schedule (id exists and is not 'new')
  const isEditing = id && id !== 'new';

  const navigateBack = useCallback(() => {
    navigate('/schedules');
  }, [navigate]);

  // Fetch existing schedule when editing
  const { data: scheduleData, isLoading: isLoadingSchedule } = useQuery({
    ...getScheduleByIdQueryOptions({ scheduleId: id }),
    enabled: !!isEditing,
  });

  // Reset form when schedule data loads
  useEffect(() => {
    if (scheduleData) {
      form.reset(mapApiToForm(scheduleData));
    }
  }, [scheduleData, form]);

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (data: ScheduleFormData) => (await ApiService.CreateSchedule(mapFormToApi(data))).json(),
    onSuccess: (data: CreateScheduleResponseDTO, variables: ScheduleFormData) => {
      toast.success('Schedule created successfully');
      navigate(getWorkspaceRoute('/schedules'), { state: { showFeedback: true, scheduleId: data.schedule.id, scheduleName: variables.name } });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to create schedule');
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async (data: ScheduleFormData) => {
      if (!id) throw new Error('Schedule ID is required');
      return (
        await ApiService.UpdateSchedule({
          scheduleId: id,
          data: mapFormToUpdateApi(data),
        })
      ).json();
    },
    onSuccess: (_, variables: ScheduleFormData) => {
      queryClient.invalidateQueries({ queryKey: scheduleKeys.all });
      queryClient.invalidateQueries({ queryKey: checkKeys.all });
      toast.success('Schedule updated successfully');
      navigate(getWorkspaceRoute('/schedules'), { state: { showFeedback: true, scheduleId: id, scheduleName: variables.name } });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update schedule');
    },
  });

  // Clone mutation
  const cloneScheduleMutation = useMutation({
    mutationFn: async () => {
      if (!id) throw new Error('No schedule ID provided');
      return await (await ApiService.CloneSchedule(id)).json();
    },
    onSuccess: (data) => {
      toast.success('Schedule cloned successfully');
      queryClient.invalidateQueries({ queryKey: scheduleKeys.all });
      navigate(`/schedules/${data.schedule.id}`, { replace: true });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to clone schedule');
    },
  });

  const isSaving = createMutation.isPending || updateMutation.isPending;

  // Archive mutation
  const archiveMutation = useMutation({
    mutationFn: async () => {
      if (!id) throw new Error('Schedule ID is required');
      return await ApiService.BulkUpdateScheduleState({ scheduleIds: [id], state: RecordState.Archived });
    },
    onSuccess: () => {
      toast.success('Schedule archived successfully');
      queryClient.invalidateQueries({ queryKey: deploymentRuleKeys.all });
      navigateBack();
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to archive schedule');
    },
  });

  const handleArchiveSchedule = () => {
    archiveMutation.mutate();
  };

  // Restore mutation
  const restoreMutation = useMutation({
    mutationFn: async () => {
      if (!id) throw new Error('Schedule ID is required');
      return await ApiService.BulkUpdateScheduleState({ scheduleIds: [id], state: RecordState.Active });
    },
    onSuccess: () => {
      toast.success('Schedule restored successfully');
      navigateBack();
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to restore schedule');
    },
  });

  // Fetch schedule forms
  const { data: scheduleFormsData } = useQuery(getScheduleFormFormsSelectQueryOptions());

  const scheduleForms = useMemo<GetScheduleFormFormsResponseDTO['data']>(() => scheduleFormsData?.data ?? [], [scheduleFormsData]);

  // Fetch workspace units
  const { data: workspaceUnitsData } = useQuery(getUnitsSelectQueryOptions());

  // Combine special time units with workspace units as grouped options
  type UnitOption = { id: string; name: string; abbreviation: string };

  const unitGroupedOptions = useMemo(() => {
    const workspaceUnits: UnitOption[] = (workspaceUnitsData?.data ?? []).map((unit) => ({
      id: unit.id,
      name: unit.name,
      abbreviation: unit.abbreviation || '',
    }));
    return [
      { label: 'Calendar', options: SPECIAL_TIME_UNITS },
      { label: 'Unit', options: workspaceUnits },
    ];
  }, [workspaceUnitsData]);

  // Flat list for lookups
  const unitOptions = useMemo<UnitOption[]>(() => unitGroupedOptions.flatMap((g) => g.options), [unitGroupedOptions]);

  // Generate preview data based on current form values
  const previewCycles = useMemo(() => {
    const getUnitAbbreviation = (unitId: string): string => {
      const unit = unitOptions.find((u) => u.id === unitId);
      return unit?.abbreviation || unitId;
    };

    // Helper to format a date for display using user's timezone settings
    const formatDate = (date: Date): string => {
      return formatDateWithUserTimezone(date, userTimezone);
    };

    const getCalendarUnit = (unitId: string): CalendarUnit | null => {
      const abbreviation = SPECIAL_TIME_UNITS.find((u) => u.id === unitId)?.abbreviation;
      return abbreviation === 'd' || abbreviation === 'w' || abbreviation === 'm' ? abbreviation : null;
    };

    const cycles: { checkLabel: string; checkIndex: number; formName: string; triggers: string[] }[][] = [];

    // Generate preview for each cycle
    for (let cycleIndex = 0; cycleIndex < previewCycleCount; cycleIndex++) {
      const cycleItems: { checkLabel: string; checkIndex: number; formName: string; triggers: string[] }[] = [];

      cycle.forEach((cycleItem, itemIndex) => {
        const checkIndex = checks.findIndex((c) => c.id === cycleItem.checkId);
        const check = checks[checkIndex];
        if (!check) return;

        const scheduleForm = scheduleForms.find((f) => f.id === check.formId);
        const multiplier = cycleIndex * cycle.length + itemIndex + 1;

        const triggerDisplays = triggers.map((t) => {
          const unitAbbrev = getUnitAbbreviation(t.unitId);
          const interval = t.interval ?? 0;

          if (!interval) {
            return `- ${unitAbbrev}`;
          }

          if (t.type === 'crawling' && isCalendarUnit(t.unitId)) {
            // Crawling + calendar: show the same interval each time
            return `${interval.toLocaleString()} ${unitAbbrev}`;
          } else if (t.type === 'fixed' && isCalendarUnit(t.unitId)) {
            // Fixed + calendar: Show dates based on startingFrom
            const calendarUnit = getCalendarUnit(t.unitId);
            if (calendarUnit && typeof t.startingFrom === 'string' && t.startingFrom) {
              const baseDate = new Date(t.startingFrom);
              const targetDate = resolveFixedCalendarDueDate(baseDate, interval, calendarUnit, multiplier - 1, t.fixedType);
              return formatDate(targetDate);
            }
            return `${interval.toLocaleString()} ${unitAbbrev}`;
          } else {
            // All non-calendar triggers (both fixed and crawling): cumulative values
            const cumulativeValue = interval * multiplier;
            return `${cumulativeValue.toLocaleString()} ${unitAbbrev}`;
          }
        });

        cycleItems.push({
          checkLabel: check.label,
          checkIndex,
          formName: scheduleForm?.name || 'No form selected',
          triggers: triggerDisplays,
        });
      });

      cycles.push(cycleItems);
    }

    return cycles;
  }, [triggers, checks, cycle, previewCycleCount, scheduleForms, unitOptions, userTimezone]);

  const handleClose = useCallback(() => {
    if (isDirty) {
      setOpenLeaveDialog(true);
    } else {
      navigateBack();
    }
  }, [isDirty, navigateBack]);

  function handleLeaveAndSave() {
    setOpenLeaveDialog(false);
    // Submit the form programmatically
    form.handleSubmit(handleSubmit)();
  }

  function handleLeaveAndDiscard() {
    navigateBack();
  }

  function handleSubmit(data: ScheduleFormData) {
    // If editing an existing schedule, show confirmation dialog
    if (isEditing) {
      setPendingFormData(data);
      setOpenSaveConfirmDialog(true);
      return;
    }
    // For new schedules, save directly
    createMutation.mutate(data);
  }

  function handleConfirmSave() {
    if (pendingFormData) {
      updateMutation.mutate(pendingFormData);
    }
    setOpenSaveConfirmDialog(false);
    setPendingFormData(null);
  }

  function handleConfirmDiscard() {
    setOpenSaveConfirmDialog(false);
    setPendingFormData(null);
  }

  function handleAddTrigger() {
    // Get unit IDs already used by existing triggers
    const usedUnitIds = triggers.map((t) => t.unitId);
    // Find the first available unit that's not already used
    const availableUnit = unitOptions.find((unit) => !usedUnitIds.includes(unit.id));

    // If no available units, don't add (shouldn't happen with max 5 triggers and usually more units available)
    if (!availableUnit) return;

    triggersFieldArray.append({
      id: `new-trigger-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
      interval: 1,
      unitId: availableUnit.id,
      type: 'crawling',
    });
  }

  function handleAddCheck() {
    if (checks.length >= 10) return; // Max 10 checks allowed
    checksFieldArray.append({
      id: `new-check-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
      label: '',
      formId: '',
    });
  }

  function handleRemoveCheck(index: number) {
    const checkId = checks[index].id;
    // Remove all cycles that reference this check (in reverse to avoid index shifting)
    const cycleIndicesToRemove = cycle
      .map((cy, i) => (cy.checkId === checkId ? i : -1))
      .filter((i) => i !== -1)
      .reverse();
    for (const cycleIndex of cycleIndicesToRemove) {
      cycleFieldArray.remove(cycleIndex);
    }
    checksFieldArray.remove(index);
  }

  function handleAddCycleItem() {
    if (checks.length === 0) return;
    cycleFieldArray.append({
      id: `new-cycle-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
      checkId: checks[0].id,
    });
  }

  function onCycleDragEnd(result: DropResult) {
    if (!result.destination) return;
    cycleFieldArray.move(result.source.index, result.destination.index);
  }

  // Set up topbar
  useLayoutEffect(() => {
    useFormTopbarStore.getState().setInfo(
      <div className="flex w-full items-center gap-1">
        <TooltipProvider>
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <ClockIcon className="bg-grey-2 rounded-md p-1" size={40} />
            </TooltipTrigger>
            <TooltipPortal>
              <TooltipContent side="bottom" sideOffset={0} className="bg-default-text pointer-events-auto text-xs font-medium text-white">
                <TooltipArrow className="fill-default-text" />
                Schedule
              </TooltipContent>
            </TooltipPortal>
          </Tooltip>
        </TooltipProvider>
        <div className="flex items-center gap-1">
          <AutoResizeInput
            value={name}
            onChange={(e) => {
              setValue('name', e.target.value, { shouldDirty: true });
            }}
            placeholder="Schedule Name"
            className="hover:ring-grey-3 focus-visible:ring-default-blue h-auto max-w-[calc(70vw)] border-none bg-transparent px-1 py-0 text-xl font-medium hover:ring-2 focus-visible:ring-2"
          />
          {id && <div className="text-secondary-text mt-[1px] text-xs">{scheduleData?.number ? EntityRefUtil.createScheduleRef(scheduleData.number) : ''}</div>}
          {scheduleData?.state === RecordState.Archived && (
            <Badge variant="orange" size="sm">
              <div className="mt-[1px] flex gap-[2px]">
                <ArchiveIcon size={15} />
                <span className="text-xs font-medium">Archived</span>
              </div>
            </Badge>
          )}
        </div>
      </div>,
    );

    useFormTopbarStore.getState().setActions(
      <div className="flex items-center gap-1">
        <TooltipProvider>
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <Button type="button" variant="ghost" aria-label="Help" className="mr-[1px]">
                <InformationIcon />
              </Button>
            </TooltipTrigger>
            <TooltipPortal>
              <TooltipContent side="bottom" sideOffset={0} className="bg-default-text pointer-events-auto text-xs font-medium text-white">
                <TooltipArrow className="fill-default-text" />
                Help
              </TooltipContent>
            </TooltipPortal>
          </Tooltip>
        </TooltipProvider>
        {id && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button type="button" variant="ghost" aria-label="More actions" className="mr-[1px]">
                <EllipsisHorizontalIcon />
              </Button>
            </DropdownMenuTrigger>
            {scheduleData?.state === RecordState.Active ? (
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setOpenArchiveConfirmation(true)} disabled={archiveMutation.isPending}>
                  <ArchiveIcon className="mr-2" />
                  Archive
                </DropdownMenuItem>
                <DropdownMenuItem className="cursor-pointer" onClick={() => cloneScheduleMutation.mutate()} disabled={!id || cloneScheduleMutation.isPending}>
                  <CloneIcon className="mr-2" />
                  Clone
                </DropdownMenuItem>
              </DropdownMenuContent>
            ) : (
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setOpenRestoreConfirmation(true)} disabled={restoreMutation.isPending}>
                  <UndoIcon className="mr-2" />
                  Restore
                </DropdownMenuItem>
              </DropdownMenuContent>
            )}
          </DropdownMenu>
        )}
        <Button className="mx-[1px] font-medium" type="submit" variant="success" disabled={!isDirty || isSaving} loading={isSaving}>
          Save
        </Button>
        <Button type="button" variant="ghost" aria-label="Close" className="mr-[1px]" onClick={handleClose}>
          <CloseIcon />
        </Button>
      </div>,
    );

    return () => {
      useFormTopbarStore.getState().setInfo(null);
      useFormTopbarStore.getState().setActions(null);
    };
  }, [
    name,
    isDirty,
    isSaving,
    navigate,
    id,
    setValue,
    handleClose,
    scheduleData?.number,
    scheduleData?.state,
    archiveMutation.isPending,
    restoreMutation,
    cloneScheduleMutation,
  ]);

  // Listen for ESC key to show warning modal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Check if ESC is pressed from within a dialog - if so, let dialog handle it
      const target = e.target as HTMLElement;
      const isInDialog = target.closest('[role="dialog"]');

      if (e.key === 'Escape' && formRef.current && !isInDialog) {
        e.preventDefault();
        handleClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleClose]);

  if (isLoadingSchedule) {
    return (
      <div className="fixed inset-0 z-30 flex h-screen w-full items-center justify-center bg-white">
        <div className="text-secondary-text">Loading schedule...</div>
      </div>
    );
  }

  return (
    <FormProvider {...form}>
      <form ref={formRef} onSubmit={form.handleSubmit(handleSubmit)} className="fixed inset-0 z-30 flex h-screen w-full flex-col bg-white">
        <FormTopbar className="border-b pb-2" />
        <ResizablePanelGroup orientation="horizontal" className="min-h-0 flex-1">
          {/* Left Panel - Editor */}
          <ResizablePanel defaultSize="520px" minSize="400px" maxSize="50%">
            <ScrollArea className="h-full">
              <div className="flex flex-col gap-4 p-4">
                {/* Triggers Section */}
                <section className="flex flex-col gap-2">
                  <div className="flex flex-col gap-1">
                    <h2 className="text-default-text text-xl font-medium">Triggers</h2>
                    <p className="text-secondary-text text-sm">
                      A check is due when <strong>ANY</strong> of these triggers are met <strong>EVERY:</strong>
                    </p>
                  </div>

                  <div className="flex flex-col gap-2">
                    {triggersFieldArray.fields.map((field, index) => (
                      <Fragment key={field.id}>
                        {index > 0 && <span className="text-secondary-text text-xs font-medium">OR</span>}
                        <div className="bg-grey-1 rounded-lg p-3">
                          <div className="mb-2 grid grid-cols-[1fr_1fr_1fr_auto] gap-2">
                            <span className="text-sm font-normal">Interval</span>
                            <span className="text-sm font-normal">Unit</span>
                            <div className="flex items-center gap-1 text-sm font-normal">
                              <span>Type</span>
                              <TooltipProvider>
                                <Tooltip delayDuration={0}>
                                  <TooltipTrigger asChild>
                                    <button type="button" aria-label="About schedule type" className="text-secondary-text">
                                      <QuestionIcon size={16} />
                                    </button>
                                  </TooltipTrigger>
                                  <TooltipPortal>
                                    <TooltipContent side="top" className="bg-default-text max-w-[240px] text-xs font-medium text-white">
                                      <TooltipArrow className="fill-default-text" />
                                      Crawling resets the interval after each check. Fixed uses a set schedule from a starting date.
                                    </TooltipContent>
                                  </TooltipPortal>
                                </Tooltip>
                              </TooltipProvider>
                            </div>
                            <div className="w-4" />
                          </div>
                          <div className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2">
                            <Controller
                              control={control}
                              name={`triggers.${index}.interval`}
                              render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
                                <NumericFormat
                                  className={cn(inputVariants({ error: isSubmitted && !!error }))}
                                  value={value ?? ''}
                                  onValueChange={(values) => {
                                    onChange(values.floatValue);
                                  }}
                                  onBlur={onBlur}
                                  allowNegative={false}
                                  decimalScale={0}
                                  thousandSeparator=","
                                  isAllowed={({ floatValue }) => floatValue === undefined || (floatValue >= 1 && floatValue <= 1000000)}
                                />
                              )}
                            />
                            <Controller
                              control={control}
                              name={`triggers.${index}.unitId`}
                              render={({ field: { value, onChange }, fieldState: { error } }) => {
                                // Get already selected unit IDs from other triggers
                                const selectedUnitIds = triggers.map((t, i) => (i !== index ? t.unitId : null)).filter((id): id is string => id !== null);

                                // Filter out already selected units
                                const availableOptions = unitGroupedOptions.map((group) => ({
                                  ...group,
                                  options: group.options.filter((opt) => !selectedUnitIds.includes(opt.id)),
                                }));

                                return (
                                  <Select<UnitOption>
                                    options={availableOptions}
                                    value={unitOptions.find((o) => o.id === value)}
                                    menuPlacement="auto"
                                    onChange={(option) => {
                                      const previousUnitId = value;
                                      const newUnitId = option?.id;
                                      onChange(newUnitId);

                                      // Seed startingFrom BEFORE deriving fixedType so the derivation
                                      // reads the freshly-seeded date. A workspace unit (e.g. Hours) has
                                      // no persisted startingFrom, so entering months must seed it first.
                                      let startingFrom = triggers[index]?.startingFrom;
                                      if (triggers[index]?.type === 'fixed' && newUnitId) {
                                        const wasCalendar = isCalendarUnit(previousUnitId);
                                        const isNowCalendar = isCalendarUnit(newUnitId);
                                        if (wasCalendar !== isNowCalendar) {
                                          startingFrom = isNowCalendar ? new Date().toISOString() : undefined;
                                          setValue(`triggers.${index}.startingFrom`, startingFrom);
                                        }
                                      }

                                      // Clear fixedType when leaving months; default it to the first
                                      // available option when entering months while already in fixed mode
                                      if (newUnitId !== 'months') {
                                        setValue(`triggers.${index}.fixedType`, undefined);
                                      } else if (triggers[index]?.type === 'fixed' && startingFrom) {
                                        const firstOption = getFixedTypeOptions(new Date(startingFrom))[0]?.value;
                                        if (firstOption) {
                                          setValue(`triggers.${index}.fixedType`, firstOption);
                                        }
                                      }
                                    }}
                                    getOptionLabel={(option) => option.name}
                                    getOptionValue={(option) => option.id}
                                    error={isSubmitted && !!error}
                                    formatGroupLabel={(group) => {
                                      const GroupIcon = group.label === 'Calendar' ? CalendarBlankIcon : GaugeIcon;
                                      return (
                                        <div className="text-secondary-text flex items-center gap-1 p-1 text-sm font-medium">
                                          <GroupIcon className="-mt-0.5" />
                                          <span>{group.label}</span>
                                        </div>
                                      );
                                    }}
                                    formatOptionLabel={(option, { context }) =>
                                      context === 'menu' ? (
                                        <div className="flex w-full items-center justify-between gap-2 text-sm">
                                          <span>{option.name}</span>
                                          <span className="lowercase">{option.abbreviation}</span>
                                        </div>
                                      ) : (
                                        option.name
                                      )
                                    }
                                    styles={{ menu: (base) => ({ ...base, width: 'max-content', minWidth: '100%' }) }}
                                  />
                                );
                              }}
                            />
                            <Controller
                              control={control}
                              name={`triggers.${index}.type`}
                              render={({ field: { value, onChange }, fieldState: { error } }) => (
                                <Select
                                  aria-label="Trigger type"
                                  options={TRIGGER_TYPE_OPTIONS}
                                  value={TRIGGER_TYPE_OPTIONS.find((o) => o.value === value)}
                                  menuPlacement="auto"
                                  onChange={(option) => {
                                    onChange(option?.value);
                                    if (option?.value === 'fixed') {
                                      const unitId = triggers[index]?.unitId;
                                      if (isCalendarUnit(unitId)) {
                                        const startingFrom = new Date();
                                        setValue(`triggers.${index}.startingFrom`, startingFrom.toISOString());
                                        if (unitId === 'months') {
                                          const firstOption = getFixedTypeOptions(startingFrom)[0]?.value;
                                          if (firstOption) {
                                            setValue(`triggers.${index}.fixedType`, firstOption);
                                          }
                                        }
                                      }
                                    }
                                  }}
                                  error={isSubmitted && !!error}
                                />
                              )}
                            />
                            <TooltipProvider>
                              <Tooltip delayDuration={0}>
                                <TooltipTrigger asChild>
                                  <button
                                    type="button"
                                    aria-label="Remove trigger"
                                    className={cn(
                                      'text-default-red mt-2 grid size-4 place-items-center',
                                      triggers.length <= 1 && 'pointer-events-none opacity-0',
                                    )}
                                    onClick={() => triggersFieldArray.remove(index)}>
                                    <CloseIcon />
                                  </button>
                                </TooltipTrigger>
                                <TooltipPortal>
                                  <TooltipContent side="right" className="bg-default-text text-xs font-medium text-white">
                                    <TooltipArrow className="fill-default-text" />
                                    Delete
                                  </TooltipContent>
                                </TooltipPortal>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                          {/* Fixed trigger additional fields - only for calendar units */}
                          {triggers[index]?.type === 'fixed' && isCalendarUnit(triggers[index]?.unitId) && (
                            <div className="mt-2 flex flex-col gap-2">
                              <div className="flex flex-col gap-1">
                                <label className="text-sm font-normal">Starting from</label>
                                <Controller
                                  control={control}
                                  name={`triggers.${index}.startingFrom`}
                                  render={({ field: { value, onChange }, fieldState: { error } }) => (
                                    <DatePicker
                                      date={typeof value === 'string' && value ? new Date(value) : undefined}
                                      setDate={(date) => {
                                        onChange(date?.toISOString());
                                        if (triggers[index]?.unitId === 'months') {
                                          setValue(`triggers.${index}.fixedType`, undefined);
                                        }
                                      }}
                                      placeholder="Select date"
                                      error={isSubmitted && !!error}
                                    />
                                  )}
                                />
                              </div>
                              {/* Only show "Repeats" field for month-based triggers */}
                              {triggers[index]?.unitId === 'months' && (
                                <div className="flex flex-col gap-1">
                                  <label className="text-sm font-normal">Repeats</label>
                                  <Controller
                                    control={control}
                                    name={`triggers.${index}.fixedType`}
                                    render={({ field: { value, onChange }, fieldState: { error } }) => {
                                      const startingFromValue = form.watch(`triggers.${index}.startingFrom`);
                                      const startingFromDate = startingFromValue ? new Date(startingFromValue) : null;

                                      const fixedTypeOptions = startingFromDate ? getFixedTypeOptions(startingFromDate) : [];

                                      return (
                                        <Select
                                          aria-label="Repeats"
                                          options={fixedTypeOptions}
                                          value={fixedTypeOptions.find((o) => o.value === value)}
                                          onChange={(option) => onChange(option?.value)}
                                          menuPlacement="auto"
                                          placeholder="Select option"
                                          isDisabled={!startingFromDate}
                                          error={isSubmitted && !!error}
                                        />
                                      );
                                    }}
                                  />
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </Fragment>
                    ))}
                    {isSubmitted && errors.triggers && <p className="text-default-red text-sm">Add at least one trigger</p>}
                  </div>

                  <Button
                    type="button"
                    variant="default"
                    className="w-fit"
                    onClick={handleAddTrigger}
                    icon={<PlusIcon />}
                    disabled={triggers.length >= 5 || !unitOptions.some((unit) => !triggers.map((t) => t.unitId).includes(unit.id))}>
                    Add Trigger
                  </Button>
                </section>

                {/* Checks Section */}
                <section className="flex flex-col gap-2">
                  <div className="flex flex-col gap-1">
                    <h2 className="text-default-text text-xl font-medium">Checks</h2>
                    <p className="text-secondary-text text-sm">Define check labels and link them to a schedule form.</p>
                  </div>

                  <table className="w-full border-separate border-spacing-y-1 [&_th:first-child]:w-1/2 [&_th:nth-child(2)]:w-1/2">
                    <thead>
                      <tr className="text-left text-sm">
                        <th className="font-normal">Check Label</th>
                        <th className="font-normal">Schedule Form</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {checksFieldArray.fields.map((field, index) => (
                        <tr key={field.id}>
                          <td className="pr-2">
                            <Controller
                              control={control}
                              name={`checks.${index}.label`}
                              render={({ field: { value, onChange }, fieldState: { error } }) => {
                                const ShapeIcon = getTimelineShapeIcon(index);
                                return (
                                  <Input
                                    value={value}
                                    onChange={(e) => onChange(e.target.value)}
                                    placeholder="Type label"
                                    prefix={<ShapeIcon size={16} />}
                                    error={isSubmitted && !!error}
                                  />
                                );
                              }}
                            />
                          </td>
                          <td className="pr-2">
                            <Controller
                              control={control}
                              name={`checks.${index}.formId`}
                              render={({ field: { value, onChange }, fieldState: { error } }) => (
                                <Select
                                  aria-label="Form"
                                  options={scheduleForms}
                                  value={scheduleForms.find((f) => f.id === value)}
                                  menuPlacement="auto"
                                  onChange={(option) => onChange(option?.id)}
                                  getOptionLabel={(option) => option.name}
                                  getOptionValue={(option) => option.id}
                                  placeholder="Select a form"
                                  error={isSubmitted && !!error}
                                />
                              )}
                            />
                          </td>
                          <td className="whitespace-nowrap">
                            <TooltipProvider>
                              <Tooltip delayDuration={0}>
                                <TooltipTrigger asChild>
                                  <button
                                    type="button"
                                    aria-label="Remove check"
                                    className={cn('text-default-red grid size-4 place-items-center', checks.length <= 1 && 'pointer-events-none opacity-0')}
                                    onClick={() => handleRemoveCheck(index)}>
                                    <CloseIcon />
                                  </button>
                                </TooltipTrigger>
                                <TooltipPortal>
                                  <TooltipContent side="right" className="bg-default-text text-xs font-medium text-white">
                                    <TooltipArrow className="fill-default-text" />
                                    Delete
                                  </TooltipContent>
                                </TooltipPortal>
                              </Tooltip>
                            </TooltipProvider>
                          </td>
                        </tr>
                      ))}
                      {isSubmitted && errors.checks?.some?.((check) => check?.formId) && (
                        <tr>
                          <td />
                          <td className="text-default-red text-sm">Select one schedule form per check</td>
                          <td />
                        </tr>
                      )}
                    </tbody>
                  </table>

                  <Button type="button" variant="default" className="w-fit" onClick={handleAddCheck} icon={<PlusIcon />} disabled={checks.length >= 10}>
                    Add Check
                  </Button>
                </section>

                {/* Cycle Section */}
                <section className="flex flex-col gap-2">
                  <div className="flex flex-col gap-1">
                    <h2 className="text-default-text text-xl font-medium">Cycle</h2>
                    <p className="text-secondary-text text-sm">Set the cycle to control the order of the checks.</p>
                  </div>

                  <DragDropContext onDragEnd={onCycleDragEnd}>
                    <Droppable droppableId="cycle-list">
                      {(provided) => (
                        <div ref={provided.innerRef} {...provided.droppableProps} className="flex flex-col gap-2">
                          {cycleFieldArray.fields.map((field, index) => {
                            return (
                              <Draggable key={field.id} draggableId={field.id} index={index}>
                                {(provided, snapshot) => (
                                  <div
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    className={cn('flex items-center gap-2', snapshot.isDragging && 'opacity-50')}>
                                    <div {...provided.dragHandleProps} className="cursor-grab">
                                      <GripDotVerticalIcon className="text-secondary-text" />
                                    </div>
                                    <Controller
                                      control={control}
                                      name={`cycle.${index}.checkId`}
                                      render={({ field: { value, onChange }, fieldState: { error } }) => {
                                        const currentCheck = checks.find((c) => c.id === value);
                                        return (
                                          <Select
                                            aria-label="Check"
                                            className="flex-1"
                                            options={checks}
                                            value={currentCheck}
                                            onChange={(option) => onChange(option?.id)}
                                            getOptionLabel={(option) => option.label || 'Select option'}
                                            getOptionValue={(option) => option.id}
                                            isSearchable={false}
                                            placeholder="Select option"
                                            error={isSubmitted && !!error}
                                            menuPlacement="auto"
                                            formatOptionLabel={(option) => {
                                              const optionIndex = checks.findIndex((c) => c.id === option.id);
                                              const ShapeIcon = getTimelineShapeIcon(optionIndex);
                                              return (
                                                <div className="flex items-center gap-2">
                                                  <ShapeIcon size={16} />
                                                  <span>{option.label || 'Select option'}</span>
                                                </div>
                                              );
                                            }}
                                          />
                                        );
                                      }}
                                    />
                                    <TooltipProvider>
                                      <Tooltip delayDuration={0}>
                                        <TooltipTrigger asChild>
                                          <button
                                            type="button"
                                            aria-label="Remove cycle"
                                            className={cn(
                                              'text-default-red grid size-4 place-items-center',
                                              cycle.length <= 1 && 'pointer-events-none opacity-0',
                                            )}
                                            onClick={() => cycleFieldArray.remove(index)}>
                                            <CloseIcon />
                                          </button>
                                        </TooltipTrigger>
                                        <TooltipPortal>
                                          <TooltipContent side="right" className="bg-default-text text-xs font-medium text-white">
                                            <TooltipArrow className="fill-default-text" />
                                            Delete
                                          </TooltipContent>
                                        </TooltipPortal>
                                      </Tooltip>
                                    </TooltipProvider>
                                  </div>
                                )}
                              </Draggable>
                            );
                          })}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>

                  {isSubmitted && errors.cycle && <p className="text-default-red text-sm">Select one check</p>}

                  <Button type="button" variant="default" className="w-fit" onClick={handleAddCycleItem} icon={<PlusIcon />} disabled={checks.length === 0}>
                    Add Cycle
                  </Button>
                </section>
              </div>
            </ScrollArea>
          </ResizablePanel>

          <ResizableHandle />

          {/* Right Panel - Preview */}
          <ResizablePanel>
            <ScrollArea className="h-full">
              <div className="bg-grey-1 min-h-full p-6">
                <div className="rounded-lg bg-white p-6 shadow-md">
                  <h2 className="text-default-text text-xl font-medium">Preview</h2>

                  <ScrollArea type="always">
                    <div>
                      <table className="w-max min-w-full border-separate border-spacing-y-2">
                        <thead>
                          <tr className="text-secondary-text text-left text-xs font-medium">
                            <th className="border-grey-3 bg-grey-1 text-default-text rounded-l border-y border-l p-2 text-sm font-semibold">Check Label</th>
                            <th className="border-grey-3 bg-grey-1 text-default-text border-y p-2 text-sm font-semibold">Schedule Form</th>
                            <th className="border-grey-3 bg-grey-1 text-default-text rounded-r border-y border-r p-2 text-sm font-medium">Triggers</th>
                          </tr>
                        </thead>

                        <tbody>
                          {previewCycles.map((cycleItems, cycleIndex) => (
                            <Fragment key={cycleIndex}>
                              {cycleItems.map((item, index) => {
                                const ShapeIcon = getTimelineShapeIcon(item.checkIndex);

                                return (
                                  <tr key={`${cycleIndex}-${index}`} className="relative">
                                    <td className="border-grey-3 relative w-0 max-w-[300px] min-w-[100px] rounded-l border-y border-l bg-white px-3 py-2 whitespace-nowrap">
                                      {/* Top connector for first item of cycles after the first */}
                                      {cycleIndex > 0 && index === 0 && <div className="bg-grey-2 absolute -top-3 left-[17.5px] h-3 w-[4px]" />}
                                      {/* Bottom connector */}
                                      <div className="bg-grey-2 absolute -bottom-3 left-[17.5px] h-3 w-[4px]" />
                                      <div className="flex items-center gap-2">
                                        <ShapeIcon size={16} className="shrink-0" />
                                        <span className="truncate text-sm font-normal">{item.checkLabel}</span>
                                      </div>
                                    </td>
                                    <td className="border-grey-3 border-y bg-white px-3 py-2 text-sm font-normal whitespace-nowrap">
                                      <div className="max-w-[200px] truncate">{item.formName}</div>
                                    </td>
                                    <td className="border-grey-3 rounded-r border-y border-r bg-white px-3 py-2">
                                      <div className="flex items-center gap-2 text-sm">
                                        {item.triggers.map((triggerDisplay, triggerIndex) => (
                                          <Fragment key={triggerIndex}>
                                            {triggerIndex > 0 && <span className="text-secondary-text shrink-0 text-center text-xs font-bold">OR</span>}
                                            <span className="min-w-[80px] shrink-0 whitespace-nowrap">{triggerDisplay}</span>
                                          </Fragment>
                                        ))}
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}

                              {/* Cycle End Indicator */}
                              <tr>
                                <td colSpan={3}>
                                  <div className="flex items-center gap-4 py-1 pl-[18px]">
                                    <div
                                      className="h-0.5 flex-1"
                                      style={{
                                        backgroundImage: 'linear-gradient(to right, rgba(var(--grey-2), 1) 12px, transparent 12px)',
                                        backgroundSize: '24px 100%',
                                      }}
                                    />
                                    <span className="text-secondary-text text-sm font-normal">Cycle end</span>
                                    <div
                                      className="h-0.5 flex-1"
                                      style={{
                                        backgroundImage: 'linear-gradient(to right, rgba(var(--grey-2), 1) 12px, transparent 12px)',
                                        backgroundSize: '24px 100%',
                                      }}
                                    />
                                  </div>
                                </td>
                              </tr>
                            </Fragment>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </ScrollArea>

                  {/* Preview More Link */}
                  <button type="button" className="text-default-blue text-sm font-medium underline" onClick={() => setPreviewCycleCount((prev) => prev + 1)}>
                    Preview more
                  </button>
                </div>
              </div>
            </ScrollArea>
          </ResizablePanel>
        </ResizablePanelGroup>

        <SaveChangesDialog
          open={openLeaveDialog}
          onOpenChange={setOpenLeaveDialog}
          saveCallback={handleLeaveAndSave}
          discardCallback={handleLeaveAndDiscard}
          loading={isSaving}
          title="Leaving without saving"
          description="Save your rule settings before leaving."
        />

        <SaveChangesDialog
          open={openSaveConfirmDialog}
          onOpenChange={setOpenSaveConfirmDialog}
          saveCallback={handleConfirmSave}
          discardCallback={handleConfirmDiscard}
          loading={updateMutation.isPending}
          title="Save changes to schedule?"
          description="Saving these changes will affect all assets deployed to the schedule. Editing the cycle will remove assets from their current position and require manual correction."
        />

        <ArchiveConfirmationDialog
          open={openArchiveConfirmation}
          onOpenChange={setOpenArchiveConfirmation}
          callback={handleArchiveSchedule}
          loading={archiveMutation.isPending}
          title="Archive schedule?"
          description="Archiving removes this schedule from all deployment rules. Assets will lose their positions and open checks will be deleted. This cannot be undone."
        />

        <RestoreConfirmationDialog
          open={openRestoreConfirmation}
          onOpenChange={setOpenRestoreConfirmation}
          callback={() => restoreMutation.mutate()}
          loading={restoreMutation.isPending}
          title="Restore schedule?"
          description="Are you sure you want to restore this schedule? It will become active again."
        />
      </form>
    </FormProvider>
  );
}

export default ScheduleEditorPage;
